import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SET_PASSWORD_TOKEN_EXPIRED_ERR,
  setNewPasswordValidationSchema,
} from "../../../constants/auth";
import { setNewPassword } from "../../../services/apiV2";
import {
  NEW_PASSWORD_SET_FAILED,
  NEW_PASSWORD_TOKEN_EXPIRED,
} from "../../../constants";
import { axiosErrorMatches } from "../../../helpers";

type TFormFields = {
  password: string;
  confirmPassword: string;
};

export type TErrors = {
  tokenExpired: string | null;
  newPasswordSetFailed: string | null;
};

type TUseSetNewPasswordProps = {
  onSuccess: () => void;
  token: string;
};

const ERRORS_INITIAL_STATE: TErrors = {
  tokenExpired: null,
  newPasswordSetFailed: null,
};

export const useSetNewPassword = ({
  onSuccess,
  token,
}: TUseSetNewPasswordProps) => {
  const [errors, setErrors] = useState<TErrors>(ERRORS_INITIAL_STATE);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TFormFields>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: zodResolver(setNewPasswordValidationSchema),
    reValidateMode: "onSubmit",
  });

  const onSubmit = handleSubmit(async ({ password, confirmPassword }) => {
    setErrors(ERRORS_INITIAL_STATE);

    try {
      await setNewPassword({ password, confirmPassword, token });
      onSuccess();
    } catch (e) {
      if (axiosErrorMatches(e, SET_PASSWORD_TOKEN_EXPIRED_ERR)) {
        setErrors((prev) => ({
          ...prev,
          tokenExpired: NEW_PASSWORD_TOKEN_EXPIRED,
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          newPasswordSetFailed: NEW_PASSWORD_SET_FAILED,
        }));
      }
    }
  });

  return {
    errors,
    control,
    isSubmitting,
    onSubmit,
  };
};
